import { Logger, createLogger } from '../../utils/Logger';
import type { ConsumableContent, EnemyContent, PassiveContent, WeaponContent } from '../ContentRegistry';

export type VisualAssetType = 'sprite' | 'spritesheet' | 'atlas' | 'tile' | 'icon';

export interface VisualAssetFrameConfig {
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly startFrame?: number;
  readonly endFrame?: number;
  readonly margin?: number;
  readonly spacing?: number;
}

export interface VisualAssetDimensions {
  readonly width: number;
  readonly height: number;
}

export interface VisualAssetEntry {
  readonly key: string;
  readonly type: VisualAssetType;
  readonly url?: string;
  readonly sourcePath?: string;
  readonly runtimePath?: string;
  readonly dimensions?: VisualAssetDimensions;
  readonly displaySize?: VisualAssetDimensions;
  readonly frameConfig?: VisualAssetFrameConfig;
  readonly required: boolean;
  readonly fallbackKey?: string;
  readonly fallbackColor?: number;
  readonly aliases?: readonly string[];
}

export interface VisualAssetReferenceValidationInput {
  readonly enemies: readonly EnemyContent[];
  readonly weapons: readonly WeaponContent[];
  readonly passives: readonly PassiveContent[];
  readonly consumables: readonly ConsumableContent[];
}

export interface VisualAssetReferenceValidationResult {
  readonly ok: boolean;
  readonly checked: {
    readonly enemySprites: readonly string[];
    readonly weaponSprites: readonly string[];
    readonly passiveIcons: readonly string[];
    readonly consumableIcons: readonly string[];
    readonly upgradeIcons: readonly string[];
  };
  readonly warnings: readonly string[];
  readonly errors: readonly string[];
}

export interface VisualPreloadSummary {
  readonly manifestKeys: readonly string[];
  readonly queuedKeys: readonly string[];
  readonly loadedKeys: readonly string[];
  readonly skippedOptionalKeys: readonly string[];
  readonly failedKeys: readonly string[];
  readonly requiredFailures: readonly string[];
}

const PROVIDED_ASSET_BASE = 'assets/sprites';

export const WEAPON_ICONS: Readonly<Record<string, string>> = Object.freeze({
  "basic_shot": "ui_basic_shot",
  "holy_wand": "ui_holy_wand",
  "orbit_shield": "ui_orbit_shield",
  "death_spiral": "ui_death_spiral",
  "area_pulse": "ui_area_pulse",
  "prism_beam": "ui_prism_beam",
  "boomerang_blade": "ui_boomerang_blade",
  "chain_arc": "ui_chain_arc"
});

export const VISUAL_ASSETS: readonly VisualAssetEntry[] = Object.freeze([
  { key: 'ui_supplies', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_supplies.png`, sourcePath: 'assets/source/ui/supplies.png', runtimePath: 'public/assets/sprites/ui_supplies.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_equipment', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_equipment.png`, sourcePath: 'assets/source/ui/equipment.png', runtimePath: 'public/assets/sprites/ui_equipment.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_weapons', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_weapons.png`, sourcePath: 'assets/source/ui/weapons.png', runtimePath: 'public/assets/sprites/ui_weapons.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_sound', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_sound.png`, sourcePath: 'assets/source/ui/sound.png', runtimePath: 'public/assets/sprites/ui_sound.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_pause', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_pause.png`, sourcePath: 'assets/source/ui/pause.png', runtimePath: 'public/assets/sprites/ui_pause.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_experience', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_experience.png`, sourcePath: 'assets/source/ui/experience.png', runtimePath: 'public/assets/sprites/ui_experience.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_health', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_health.png`, sourcePath: 'assets/source/ui/health.png', runtimePath: 'public/assets/sprites/ui_health.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_chain_arc', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_chain_arc.png`, sourcePath: 'assets/source/ui/chain_arc.png', runtimePath: 'public/assets/sprites/ui_chain_arc.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_boomerang_blade', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_boomerang_blade.png`, sourcePath: 'assets/source/ui/boomerang_blade.png', runtimePath: 'public/assets/sprites/ui_boomerang_blade.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_prism_beam', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_prism_beam.png`, sourcePath: 'assets/source/ui/prism_beam.png', runtimePath: 'public/assets/sprites/ui_prism_beam.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_area_pulse', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_area_pulse.png`, sourcePath: 'assets/source/ui/area_pulse.png', runtimePath: 'public/assets/sprites/ui_area_pulse.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_death_spiral', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_death_spiral.png`, sourcePath: 'assets/source/ui/death_spiral.png', runtimePath: 'public/assets/sprites/ui_death_spiral.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_orbit_shield', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_orbit_shield.png`, sourcePath: 'assets/source/ui/orbit_shield.png', runtimePath: 'public/assets/sprites/ui_orbit_shield.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_holy_wand', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_holy_wand.png`, sourcePath: 'assets/source/ui/holy_wand.png', runtimePath: 'public/assets/sprites/ui_holy_wand.png', dimensions: { width: 96, height: 96 }, required: false },
  { key: 'ui_basic_shot', type: 'icon', url: `${PROVIDED_ASSET_BASE}/ui_basic_shot.png`, sourcePath: 'assets/source/ui/basic_shot.png', runtimePath: 'public/assets/sprites/ui_basic_shot.png', dimensions: { width: 96, height: 96 }, required: false },
  {
    key: 'menu_swamp', type: 'sprite', url: `${PROVIDED_ASSET_BASE}/menu_swamp.png`,
    sourcePath: 'assets/source/ui/menu-swamp.png', runtimePath: 'public/assets/sprites/menu_swamp.png',
    required: false
  },
  ...['environment', 'emblem'].map(name => ({
    key: `menu_${name}`, type: 'sprite' as const, url: `${PROVIDED_ASSET_BASE}/menu_${name}.png`,
    sourcePath: `assets/source/ui/menu-${name}.png`, runtimePath: `public/assets/sprites/menu_${name}.png`, required: false
  })),
  ...['heal', 'coil', 'beam'].map(name => ({
    key: `ui_sample_${name}`, type: 'icon' as const, url: `${PROVIDED_ASSET_BASE}/ui_sample_${name}.png`,
    sourcePath: `assets/source/ui/sample-${name}.png`, runtimePath: `public/assets/sprites/ui_sample_${name}.png`,
    dimensions: { width: 96, height: 96 }, required: false
  })),
  {
    key: 'player_default',
    type: 'sprite',
    url: `${PROVIDED_ASSET_BASE}/player_default.png`,
    sourcePath: 'assets/source/characters/player_default.png',
    runtimePath: 'public/assets/sprites/player_default.png',
    dimensions: { width: 400, height: 400 },
    displaySize: { width: 38, height: 38 },
    required: true,
    fallbackKey: 'player',
    fallbackColor: 0x6fa51f,
    aliases: ['player']
  },
  {
    key: 'enemy_basic',
    type: 'sprite',
    url: `${PROVIDED_ASSET_BASE}/enemy_basic.png`,
    sourcePath: 'assets/source/enemies/enemy_basic.png',
    runtimePath: 'public/assets/sprites/enemy_basic.png',
    dimensions: { width: 400, height: 440 },
    displaySize: { width: 36, height: 40 },
    required: true,
    fallbackKey: 'enemy',
    fallbackColor: 0x6fa51f,
    aliases: ['enemy', 'basic_enemy']
  },
  {
    key: 'enemy_fast',
    type: 'sprite',
    url: `${PROVIDED_ASSET_BASE}/enemy_fast.png`,
    sourcePath: 'assets/source/enemies/enemy_fast.png',
    runtimePath: 'public/assets/sprites/enemy_fast.png',
    dimensions: { width: 400, height: 430 },
    displaySize: { width: 34, height: 37 },
    required: true,
    fallbackKey: 'enemy',
    fallbackColor: 0x6fa51f,
    aliases: ['fast_enemy']
  },
  {
    key: 'enemy_tank',
    type: 'sprite',
    url: `${PROVIDED_ASSET_BASE}/enemy_tank.png`,
    sourcePath: 'assets/source/enemies/enemy_tank.png',
    runtimePath: 'public/assets/sprites/enemy_tank.png',
    dimensions: { width: 400, height: 500 },
    displaySize: { width: 40, height: 50 },
    required: true,
    fallbackKey: 'enemy',
    fallbackColor: 0x8cc629,
    aliases: ['tank_enemy']
  },
  { key: 'player', type: 'sprite', required: false, fallbackKey: 'player_default', fallbackColor: 0x3366ff },
  { key: 'enemy', type: 'sprite', required: false, fallbackKey: 'enemy_basic', fallbackColor: 0xffffff },
  { key: 'bullet', type: 'sprite', required: false, fallbackColor: 0xffff00 },
  { key: 'orbit_orb', type: 'sprite', required: false, fallbackColor: 0x00ffff },
  { key: 'weapon_area', type: 'sprite', required: false, fallbackColor: 0xff8844 },
  { key: 'weapon_beam', type: 'sprite', required: false, fallbackColor: 0x88ccff },
  { key: 'weapon_boomerang', type: 'sprite', required: false, fallbackColor: 0x99ffcc },
  { key: 'weapon_chain', type: 'sprite', required: false, fallbackColor: 0xffff99 },
  { key: 'gem', type: 'sprite', required: false, fallbackColor: 0x00ff00 },
  { key: 'chest', type: 'sprite', required: false, fallbackColor: 0xc0822d },
  { key: 'ground', type: 'tile', required: false, fallbackColor: 0xffffff },
  { key: 'ground_grid', type: 'tile', required: false, fallbackColor: 0xffffff },
  { key: 'passive_armor', url: `${PROVIDED_ASSET_BASE}/passive_armor.png`, sourcePath: 'assets/source/ui/armor.png', runtimePath: 'public/assets/sprites/passive_armor.png', dimensions: { width: 96, height: 96 },  type: 'icon', required: false, fallbackKey: 'gem', fallbackColor: 0xffc928 },
  { key: 'passive_haste', url: `${PROVIDED_ASSET_BASE}/passive_haste.png`, sourcePath: 'assets/source/ui/haste.png', runtimePath: 'public/assets/sprites/passive_haste.png', dimensions: { width: 96, height: 96 },  type: 'icon', required: false, fallbackKey: 'gem', fallbackColor: 0x42aaf5 },
  { key: 'passive_magnet', url: `${PROVIDED_ASSET_BASE}/passive_magnet.png`, sourcePath: 'assets/source/ui/magnet.png', runtimePath: 'public/assets/sprites/passive_magnet.png', dimensions: { width: 96, height: 96 },  type: 'icon', required: false, fallbackKey: 'gem', fallbackColor: 0xab46dc },
  { key: 'passive_might', url: `${PROVIDED_ASSET_BASE}/passive_might.png`, sourcePath: 'assets/source/ui/might.png', runtimePath: 'public/assets/sprites/passive_might.png', dimensions: { width: 96, height: 96 },  type: 'icon', required: false, fallbackKey: 'gem', fallbackColor: 0xef4f50 },
  { key: 'passive_speed', url: `${PROVIDED_ASSET_BASE}/passive_speed.png`, sourcePath: 'assets/source/ui/speed.png', runtimePath: 'public/assets/sprites/passive_speed.png', dimensions: { width: 96, height: 96 },  type: 'icon', required: false, fallbackKey: 'gem', fallbackColor: 0x66ccaa },
  { key: 'consumable_gold', url: `${PROVIDED_ASSET_BASE}/consumable_gold.png`, sourcePath: 'assets/source/ui/gold_25.png', runtimePath: 'public/assets/sprites/consumable_gold.png', dimensions: { width: 96, height: 96 },  type: 'icon', required: false, fallbackKey: 'gem', fallbackColor: 0xffcc4f },
  { key: 'consumable_heal', url: `${PROVIDED_ASSET_BASE}/consumable_heal.png`, sourcePath: 'assets/source/ui/heal_20.png', runtimePath: 'public/assets/sprites/consumable_heal.png', dimensions: { width: 96, height: 96 },  type: 'icon', required: false, fallbackKey: 'gem', fallbackColor: 0x4cb250 },
  { key: 'boss', type: 'sprite', required: false, fallbackKey: 'enemy_tank', aliases: ['void_tyrant', 'iron_colossus'] }
]);

let currentPreloadSummary: VisualPreloadSummary = Object.freeze({
  manifestKeys: VISUAL_ASSETS.map(asset => asset.key),
  queuedKeys: [],
  loadedKeys: [],
  skippedOptionalKeys: VISUAL_ASSETS.filter(asset => !asset.url && !asset.required).map(asset => asset.key),
  failedKeys: [],
  requiredFailures: []
});

export function getVisualAssetManifest(logger: Logger = createLogger('VisualAssets')): readonly VisualAssetEntry[] {
  logger.debug('manifest:entries', {
    assets: VISUAL_ASSETS.map(asset => ({
      key: asset.key,
      type: asset.type,
      path: asset.url,
      required: asset.required,
      aliases: asset.aliases ?? []
    }))
  });
  return VISUAL_ASSETS;
}

export function getVisualAssetEntry(key: string): VisualAssetEntry | undefined {
  return VISUAL_ASSETS.find(asset => asset.key === key || asset.aliases?.includes(key));
}

export function hasVisualAssetOrAlias(key: string): boolean {
  return Boolean(getVisualAssetEntry(key));
}

export function updateVisualPreloadSummary(summary: VisualPreloadSummary): void {
  currentPreloadSummary = Object.freeze({
    manifestKeys: [...summary.manifestKeys],
    queuedKeys: [...summary.queuedKeys],
    loadedKeys: [...summary.loadedKeys],
    skippedOptionalKeys: [...summary.skippedOptionalKeys],
    failedKeys: [...summary.failedKeys],
    requiredFailures: [...summary.requiredFailures]
  });
}

export function getVisualPreloadSummary(): VisualPreloadSummary {
  return currentPreloadSummary;
}

export function validateContentVisualReferences(
  input: VisualAssetReferenceValidationInput,
  logger: Logger = createLogger('VisualAssets')
): VisualAssetReferenceValidationResult {
  const checked = {
    enemySprites: uniqueSorted(input.enemies.map(enemy => enemy.sprite)),
    weaponSprites: uniqueSorted(input.weapons.map(weapon => weapon.sprite)),
    passiveIcons: uniqueSorted(input.passives.map(passive => passive.icon)),
    consumableIcons: uniqueSorted(input.consumables.map(consumable => consumable.icon)),
    upgradeIcons: uniqueSorted(input.weapons.map(weapon => weapon.sprite))
  };
  const warnings: string[] = [];
  const errors: string[] = [];

  const requiredRefs = [
    ...checked.enemySprites.map(key => ({ key, category: 'enemySprites' })),
    ...checked.weaponSprites.map(key => ({ key, category: 'weaponSprites' }))
  ];
  const optionalRefs = [
    ...checked.passiveIcons.map(key => ({ key, category: 'passiveIcons' })),
    ...checked.consumableIcons.map(key => ({ key, category: 'consumableIcons' })),
    ...checked.upgradeIcons.map(key => ({ key, category: 'upgradeIcons' }))
  ];

  for (const ref of requiredRefs) {
    if (!hasVisualAssetOrAlias(ref.key)) {
      errors.push(`Missing required visual manifest entry for ${ref.category}: ${ref.key}`);
    }
  }

  for (const ref of optionalRefs) {
    if (!hasVisualAssetOrAlias(ref.key)) {
      warnings.push(`Missing optional visual manifest entry for ${ref.category}: ${ref.key}`);
    }
  }

  logger.debug('validateContentVisualReferences:summary', checked);
  warnings.forEach(warning => logger.warn('validateContentVisualReferences:optional-fallback', { warning }));
  if (errors.length > 0) {
    logger.error('validateContentVisualReferences:required-missing', { errors });
  }

  return {
    ok: errors.length === 0,
    checked,
    warnings,
    errors
  };
}

function uniqueSorted(values: readonly string[]): readonly string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}
